import React, { useEffect, useState } from 'react';

/**
 * RadialOrbitalTimeline — minimalist single-orbit constellation.
 *
 * Six labelled nodes orbit a pulsing centre marker at a slow,
 * continuous rotation. Labels only — no status, energy, or detail
 * cards. Renders directly on the page background (transparent).
 */

const NODES = [
    { id: 1, title: 'Problem Solving' },
    { id: 2, title: 'Execution & Delivery' },
    { id: 3, title: 'Communication' },
    { id: 4, title: 'Ownership & Initiative' },
    { id: 5, title: 'Learning Speed' },
    { id: 6, title: 'Collaboration' },
];

const RADIUS = 220;             // px, orbit radius
const NODE_SIZE = 40;           // px, node circle diameter
const CENTRE_SIZE = 48;         // px, centre circle diameter
const ROTATION_STEP_DEG = 0.18; // per tick
const TICK_MS = 50;             // ~3.6°/sec

const RadialOrbitalTimeline = () => {
    const [rotationAngle, setRotationAngle] = useState(0);

    useEffect(() => {
        // Honour the user's reduced-motion preference: skip the rotation interval
        // entirely so the orbit renders as a static snapshot.
        if (typeof window !== 'undefined' && window.matchMedia) {
            const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
            if (mq.matches) return undefined;
        }
        const id = setInterval(() => {
            setRotationAngle((prev) => Number(((prev + ROTATION_STEP_DEG) % 360).toFixed(3)));
        }, TICK_MS);
        return () => clearInterval(id);
    }, []);

    return (
        <div
            className="relative w-full h-full"
            data-testid="radial-orbital-timeline"
        >
            <div className="absolute inset-0 flex items-center justify-center">
                {/* Orbit ring */}
                <div
                    className="absolute rounded-full"
                    style={{
                        width: RADIUS * 2,
                        height: RADIUS * 2,
                        border: '1px solid #8B5CF6',
                        opacity: 0.3,
                    }}
                    data-testid="orbit-ring"
                />

                {/* Centre marker */}
                <div className="relative flex items-center justify-center" data-testid="orbital-centre">
                    <div
                        className="absolute rounded-full animate-ping"
                        style={{ width: CENTRE_SIZE + 24, height: CENTRE_SIZE + 24, backgroundColor: '#8B5CF6', opacity: 0.25 }}
                    />
                    <div
                        className="absolute rounded-full animate-pulse"
                        style={{ width: CENTRE_SIZE + 8, height: CENTRE_SIZE + 8, backgroundColor: '#8B5CF6', opacity: 0.4 }}
                    />
                    <div
                        className="relative flex items-center justify-center rounded-full"
                        style={{ width: CENTRE_SIZE, height: CENTRE_SIZE, backgroundColor: '#8B5CF6' }}
                    >
                        <span
                            style={{
                                fontFamily: 'Unbounded, sans-serif',
                                fontWeight: 700,
                                fontSize: '1.5rem',
                                color: '#FFFFFF',
                                lineHeight: 1,
                            }}
                        >
                            S
                        </span>
                    </div>
                </div>

                {/* Nodes */}
                {NODES.map((item, index) => {
                    const angle = ((index / NODES.length) * 360 + rotationAngle) % 360;
                    const rad = (angle * Math.PI) / 180;
                    const x = RADIUS * Math.cos(rad);
                    const y = RADIUS * Math.sin(rad);
                    const opacity = Math.max(0.6, Math.min(1, 0.6 + 0.4 * ((1 + Math.sin(rad)) / 2)));
                    return (
                        <div
                            key={item.id}
                            className="absolute"
                            style={{
                                transform: `translate(${x}px, ${y}px)`,
                                opacity,
                                transition: 'opacity 0.5s linear',
                            }}
                            data-testid={`orbital-node-${item.id}`}
                        >
                            <div className="relative flex flex-col items-center">
                                <div
                                    className="flex items-center justify-center rounded-full"
                                    style={{
                                        width: NODE_SIZE,
                                        height: NODE_SIZE,
                                        backgroundColor: 'rgba(16, 185, 129, 0.15)',
                                        border: '2px solid #10B981',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontFamily: 'Unbounded, sans-serif',
                                            fontWeight: 700,
                                            fontSize: '14px',
                                            color: '#10B981',
                                            lineHeight: 1,
                                        }}
                                    >
                                        {item.title.charAt(0)}
                                    </span>
                                </div>
                                <div
                                    className="mt-2 whitespace-nowrap"
                                    style={{
                                        fontFamily: 'Unbounded, sans-serif',
                                        fontSize: '11px',
                                        color: '#0A0A0A',
                                    }}
                                >
                                    {item.title}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RadialOrbitalTimeline;
